import React, {useCallback, useContext, useEffect, useState} from 'react';
import { ButtonProps } from '@material-ui/core/Button';
import { useFormikContext } from 'formik';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { push } from 'connected-react-router';

import { Button } from '..';
import {
  checkChangeResource, getPreventCancelText, getPreventLeaveText,
} from '../modal-prevent/utils';
import ModalPreventForm from '../modal-prevent/ModalPreventForm';
import { clipLastSegmentRoute } from '../../utility/path';
import hotKeyConverter from '../../utility/keyCodeConverter';
import { DeferredActionContext } from '../../services/common/DeferredActionContext';

type Props = ButtonProps & {
  dirty: boolean;
  withHotkey?: boolean;
  preventCancelChanges?: boolean;
  creatingMode?: boolean;
};

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  root: {
    '&:hover': {
      backgroundColor: palette.cancelAction.background,
    },
  },
  disabled: palette.disabledButton,
}), { name: 'submit' });

const FormResetWithConfirm = ({
  children,
  onClick,
  dirty,
  withHotkey,
  preventCancelChanges = false,
  creatingMode = false,
  ...props
}: Props) => {
  const styles = useStyles();
  const { clearActions, processActions } = useContext(DeferredActionContext);
  const [showModal, setShowModal] = useState(false);
  const formContext = useFormikContext();
  const dispatch = useDispatch();
  const [redirectOpen, setRedirectOpen] = useState(false);
  const [focusCancelButton, setFocusCancelButton] = useState(false);
  const { url } = useRouteMatch();
  const resetForm = useCallback(() => {
    formContext.resetForm();
    setShowModal(false);

    if (creatingMode) {
      setTimeout(() => {
        dispatch(push(clipLastSegmentRoute(url)));
      }, 500);
    }
  }, [formContext, dispatch, url, creatingMode]);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (event.keyCode === hotKeyConverter('esc')) {
        event.preventDefault();
        if (dirty) {
          if (!showModal && !redirectOpen) {
            setFocusCancelButton(false);
            setShowModal(true);
          }
        } else {
          resetForm();
        }
      }
    };
    if (withHotkey) {
      window.addEventListener('keydown', hotKeyHandler);
    }
    return () => {
      if (withHotkey) {
        window.removeEventListener('keydown', hotKeyHandler);
      }
    };
  }, [
    withHotkey,
    formContext,
    setShowModal,
    redirectOpen,
    showModal,
    setFocusCancelButton,
    dirty,
    resetForm,
  ]);

  const texts = getPreventCancelText();
  const cancelChanges = useCallback(() => {
    if (dirty) {
      setShowModal(true);
      setFocusCancelButton(true);
    } else {
      resetForm();
    }
  }, [setShowModal, setFocusCancelButton, dirty, resetForm]);

  // set redirect modal
  const [nextRoute, setNextRoute] = useState('');
  const { block } = useHistory();
  const redirectTexts = getPreventLeaveText();

  const approveRedirectHandler = useCallback(() => {
    clearActions();
    setRedirectOpen((prev) => !prev);
  }, [setRedirectOpen, clearActions]);

  const cancelRedirectHandler = useCallback(() => {
    setRedirectOpen((prev) => !prev);
    dispatch(push(nextRoute));
    processActions();
  }, [setRedirectOpen, nextRoute, dispatch, processActions]);

  useEffect(() => {
    const unregister = block((e) => {
      if (!redirectOpen && preventCancelChanges && checkChangeResource(url, e.pathname)) {
        setNextRoute(e.pathname);
        setRedirectOpen(true);
        return false;
      }
    });

    return () => unregister();
  }, [setRedirectOpen, setNextRoute, block, url, preventCancelChanges, redirectOpen]);
  // end redirect modal

  return (
    <>
      {showModal && !redirectOpen
        && (
          <ModalPreventForm
            open={showModal}
            cancelHandler={resetForm}
            approveHandler={() => { setShowModal((show) => !show); }}
            onEscapeKeyDown={() => {
              setTimeout(() => { setShowModal((show) => !show); }, 250);
            }}
            focusCancelButton={focusCancelButton}
            title={texts.title}
            message={texts.message}
            approveText={texts.approveText}
            cancelText={texts.cancelText}
          />
        )}
      {
        redirectOpen && preventCancelChanges
        && (
          <ModalPreventForm
            open={redirectOpen}
            cancelHandler={cancelRedirectHandler}
            approveHandler={approveRedirectHandler}
            onEscapeKeyDown={() => { setTimeout(() => { approveRedirectHandler(); }, 250); }}
            title={redirectTexts.title}
            message={redirectTexts.message}
            approveText={redirectTexts.approveText}
            cancelText={redirectTexts.cancelText}
          />
        )
      }

      <Button
        type="button"
        onClick={cancelChanges}
        classes={{
          ...styles,
        }}
        {...props}
      >
        {children}
      </Button>
    </>
  );
};

export default FormResetWithConfirm;
